/**
 * Admin registries + operational configuration (S113; M12). The operator edits country/region/market/category/
 * geofence/venue settings as **versioned, effective-dated, scoped** `@vello/config` `PolicySetting`s, then runs
 * a governance workflow — draft → pending approval → approved → published (with a scheduled effective date) →
 * rollback — under four-eyes RBAC (the approver may not be the author). A diff shows exactly what a publish
 * changes, and a **lower-scope constraint guard** refuses an edit that would loosen a `FIXED`/protected parent
 * (legal/safety/privacy/tax/ledger/eligibility/payment) — the AGENTS.md non-negotiable. Persistence of a
 * published version is a backend residual (no admin config write endpoint in M11); the workflow + guards run
 * here and CI-provably.
 */
import { type Result, ok, err } from '@vello/core';
import {
  type ConfigApi,
  type CountryPackView,
  type CreateCountryPackInput,
  type ApproveCountryPackInput,
  type CountryPackRetirementView,
  type CountryPackAuditView,
  type ProposeCountryPackRetirementInput,
  type DecideCountryPackRetirementInput,
  type ApiError,
} from '@vello/api-client';

export type RegistryEntityKind = 'country' | 'region' | 'market' | 'category' | 'geofence' | 'venue';

// D401: the client-side constraint guard, the setting diff and the four-eyes governance reducer that used to
// live here were never mounted by a screen — the real governance runs on the server (`config-routes.ts`, the D366
// attestation path and the W6-SETTINGS-CORE kernel), which is the only place that can enforce it. They were
// removed with their tests rather than kept as a second, unreachable implementation of the same rules.

// ---- country registration form (the "register a country" vertical) -------------------------------

/**
 * The country-registration form's raw fields as the operator types them. Dates are calendar days
 * (`YYYY-MM-DD`); the normalizer anchors them to UTC midnight so a pack's effective window is unambiguous and
 * timezone-free. Neutral by construction: nothing here is pre-filled with a country, currency, or locale.
 */
export interface CountryPackForm {
  readonly countryCode: string;
  readonly currency: string;
  readonly locale: string;
  /** Comma/space-separated BCP-47 tags; empty means "just the recommended locale" (server default). */
  readonly supportedLocales: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
  // ---- Layer 1: capabilities/policy (published as country policy on approve) ----
  /** Comma/space-separated universal product capability tokens (e.g. `ride, moto, delivery`). */
  readonly productCapabilities: string;
  /** Comma/space-separated universal payment capability tokens (e.g. `instant_payment, card, cash`). */
  readonly paymentCapabilities: string;
  /** Data-protection framework reference (e.g. `GDPR`, `LGPD`). Free text. */
  readonly privacyFramework: string;
  /** Data-protection regulator reference (e.g. `ANPD`, `CNIL`). Free text. */
  readonly privacyRegulator: string;
}

export type CountryFormField =
  | 'countryCode'
  | 'currency'
  | 'locale'
  | 'supportedLocales'
  | 'effectiveFrom'
  | 'effectiveTo'
  | 'productCapabilities'
  | 'paymentCapabilities';

/** A field-level validation problem with a stable reason code (rendered as a localized message). */
export interface CountryFormIssue {
  readonly field: CountryFormField;
  readonly code:
    | 'INVALID_COUNTRY'
    | 'INVALID_CURRENCY'
    | 'INVALID_LOCALE'
    | 'INVALID_SUPPORTED_LOCALE'
    | 'INVALID_DATE'
    | 'NOT_AFTER_FROM'
    | 'INVALID_CAPABILITY';
}

export function emptyCountryPackForm(): CountryPackForm {
  return {
    countryCode: '', currency: '', locale: '', supportedLocales: '', effectiveFrom: '', effectiveTo: '',
    productCapabilities: '', paymentCapabilities: '', privacyFramework: '', privacyRegulator: '',
  };
}

const CC_ALPHA2 = /^[A-Z]{2}$/;
const CUR_ISO4217 = /^[A-Z]{3}$/;
const BCP47 = /^[a-zA-Z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;
const CAL_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Universal capability tokens are lowercase snake identifiers (`ride`, `instant_payment`); never a country literal. */
const CAP_TOKEN = /^[a-z][a-z0-9_]*$/;

/** Split a comma/space-separated token list into trimmed non-empty items. */
function splitTokens(raw: string): readonly string[] {
  return raw.split(/[,\s]+/).map((s) => s.trim()).filter((s) => s !== '');
}

/**
 * Validate + normalize the form into the wire `CreateCountryPackInput`, or return every field issue at once.
 * Pure and framework-neutral (tested in Node): the screen renders the issues and the server re-validates the
 * assigned-currency allowlist and effective window. `countryCode`/`currency` are upper-cased; calendar days
 * become UTC-midnight instants; supported locales are split on commas/whitespace.
 */
export function normalizeCountryPackForm(form: CountryPackForm): Result<CreateCountryPackInput, readonly CountryFormIssue[]> {
  const issues: CountryFormIssue[] = [];
  const cc = form.countryCode.trim().toUpperCase();
  if (!CC_ALPHA2.test(cc)) issues.push({ field: 'countryCode', code: 'INVALID_COUNTRY' });
  const currency = form.currency.trim().toUpperCase();
  if (!CUR_ISO4217.test(currency)) issues.push({ field: 'currency', code: 'INVALID_CURRENCY' });
  const locale = form.locale.trim();
  if (!BCP47.test(locale)) issues.push({ field: 'locale', code: 'INVALID_LOCALE' });
  const supportedLocales = splitTokens(form.supportedLocales);
  if (supportedLocales.some((l) => !BCP47.test(l))) issues.push({ field: 'supportedLocales', code: 'INVALID_SUPPORTED_LOCALE' });
  const from = form.effectiveFrom.trim();
  if (!CAL_DATE.test(from)) issues.push({ field: 'effectiveFrom', code: 'INVALID_DATE' });
  const to = form.effectiveTo.trim();
  if (to !== '' && !CAL_DATE.test(to)) issues.push({ field: 'effectiveTo', code: 'INVALID_DATE' });
  // Calendar days compare lexicographically; both anchor to UTC midnight, so this matches the server's instant test.
  if (CAL_DATE.test(from) && to !== '' && CAL_DATE.test(to) && to <= from) issues.push({ field: 'effectiveTo', code: 'NOT_AFTER_FROM' });
  // Layer 1 capabilities/policy (optional; published as country policy on approve).
  const productCapabilities = splitTokens(form.productCapabilities);
  if (productCapabilities.some((t) => !CAP_TOKEN.test(t))) issues.push({ field: 'productCapabilities', code: 'INVALID_CAPABILITY' });
  const paymentCapabilities = splitTokens(form.paymentCapabilities);
  if (paymentCapabilities.some((t) => !CAP_TOKEN.test(t))) issues.push({ field: 'paymentCapabilities', code: 'INVALID_CAPABILITY' });
  const privacyFramework = form.privacyFramework.trim();
  const privacyRegulator = form.privacyRegulator.trim();
  if (issues.length > 0) return err(issues);
  return ok({
    countryCode: cc,
    currency,
    locale,
    ...(supportedLocales.length > 0 ? { supportedLocales } : {}),
    effectiveFrom: `${from}T00:00:00.000Z`,
    ...(to !== '' ? { effectiveTo: `${to}T00:00:00.000Z` } : {}),
    ...(productCapabilities.length > 0 ? { productCapabilities } : {}),
    ...(paymentCapabilities.length > 0 ? { paymentCapabilities } : {}),
    ...(privacyFramework !== '' ? { privacyFramework } : {}),
    ...(privacyRegulator !== '' ? { privacyRegulator } : {}),
  });
}

// ---- country-pack runtime helpers (thin seams over the real operator config endpoints) -----------

interface ConfigRuntime {
  readonly config: ConfigApi;
}

/** Register a country as a new DRAFT pack version. */
export function createCountryDraft(runtime: ConfigRuntime, input: CreateCountryPackInput): Promise<Result<{ created: boolean; pack: CountryPackView }, ApiError>> {
  return runtime.config.createCountryPackDraft(input);
}

/** Read a country's pack version history (what a create/approve produced). */
export function listCountryPacks(runtime: ConfigRuntime, countryCode: string): Promise<Result<{ country: string; packs: readonly CountryPackView[]; retirements?: readonly CountryPackRetirementView[]; audit?: readonly CountryPackAuditView[] }, ApiError>> {
  return runtime.config.listCountryPacks(countryCode);
}

/** Approve a DRAFT version (four-eyes enforced server-side: the approver may not be the author). On approval the
 *  server publishes the country's policy settings; `policySettingsPublished` reports how many became effective. */
export function approveCountryPack(runtime: ConfigRuntime, countryCode: string, input: ApproveCountryPackInput): Promise<Result<{ approved: CountryPackView; policySettingsPublished: number }, ApiError>> {
  return runtime.config.approveCountryPack(countryCode, input);
}

// ---- D420 (owner decisions D408 #12, D422): retire a country pack under four eyes -----------------------------

/**
 * Why a pack is retired — the closed vocabulary the server accepts, in the order the confirm dialog offers it (the
 * last one is the catch-all). The operator's written reason travels beside it.
 */
export const PACK_RETIRE_REASONS: readonly ProposeCountryPackRetirementInput['reasonCode'][] = [
  'pack_test_registration',
  'pack_registered_in_error',
  'pack_superseded',
  'pack_country_exit',
  'pack_other',
];

/** The pending retirement of a version, if any (the server keeps one PENDING proposal per version). */
export function pendingRetirement(retirements: readonly CountryPackRetirementView[] | undefined, version: number): CountryPackRetirementView | undefined {
  return retirements?.find((r) => r.version === version && r.state === 'PENDING');
}

/**
 * What the signed-in operator may do with a version's retirement: propose it (an APPROVED version with none pending),
 * approve or reject someone else's proposal, or only wait/withdraw its own (four eyes — the server refuses the proposer's
 * approval anyway; the console never offers it). Case- and space-insensitive, like the server.
 */
export type RetireAction = 'propose' | 'decide' | 'own_pending' | 'none';

/** D868: `selfApproval` (a GLOBAL super administrator) decides its own pending retirement too (approve, or reject = withdraw). */
export function retireAction(pack: CountryPackView, retirements: readonly CountryPackRetirementView[] | undefined, meId: string | undefined, selfApproval = false): RetireAction {
  if (pack.status !== 'APPROVED') return 'none';
  const pending = pendingRetirement(retirements, pack.version);
  if (pending === undefined) return 'propose';
  const same = meId !== undefined && meId.trim().toLowerCase() === pending.proposedBy.trim().toLowerCase();
  return same && !selfApproval ? 'own_pending' : 'decide';
}

export function proposeCountryPackRetirement(runtime: ConfigRuntime, countryCode: string, input: ProposeCountryPackRetirementInput): Promise<Result<{ retirement: CountryPackRetirementView }, ApiError>> {
  return runtime.config.proposeCountryPackRetirement(countryCode, input);
}

export function approveCountryPackRetirement(runtime: ConfigRuntime, countryCode: string, input: DecideCountryPackRetirementInput): Promise<Result<{ retirement: CountryPackRetirementView; retired?: CountryPackView }, ApiError>> {
  return runtime.config.approveCountryPackRetirement(countryCode, input);
}

export function rejectCountryPackRetirement(runtime: ConfigRuntime, countryCode: string, input: DecideCountryPackRetirementInput): Promise<Result<{ retirement: CountryPackRetirementView }, ApiError>> {
  return runtime.config.rejectCountryPackRetirement(countryCode, input);
}
